import { ListItemCardMobile } from "@components/common/mobile/list-item-card-mobile";
import { SkeletonProjectListItemCard } from "@components/common/mobile/skeleton-project-list-item-card";
import { FaroTableBulkActions } from "@components/common/faro-table/faro-table-bulk-actions";
import { GROUPS_SORT_BY_MAP } from "@components/groups/groups-constants";
import { VirtualizedGrid } from "@components/virtualized/virtualized-grid";
import { GroupsSortBy, GroupTypes } from "@custom-types/group-types";
import { Box, Divider } from "@mui/material";
import { useGroupsPageBulkActions } from "@pages/groups/use-groups-page-bulk-actions";
import { useGroupMenuOptionsMobile } from "@pages/groups/use-group-menu-options-mobile";
import { generateGroupDetailsRoute } from "@router/router-helper";
import { selectedEntitiesSelector } from "@store/table/table-selector";
import { selectedSdbCompanySelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { getProjectNumbersInGroup, getMemberCount } from "@utils/group-utils";
import defaultGroupThumbnail from "@assets/thumbnails/groups-thumbs/group-thumbnail_default.svg";
import { useDateTime } from "@hooks/use-date-time";
import { CreateGroup } from "@components/groups/create-group";

interface Props {
  /** The groups that are shown */
  groups: GroupTypes[];

  /** Whether the data for showing a table is loading or not */
  isLoading: boolean;

  /** Groups sort criteria */
  sortBy: GroupsSortBy;
}

/**
 * Mobile-specific view for groups list with action bar and card layout.
 */
export function GroupsMobileView({
  groups,
  sortBy,
  isLoading,
}: Props): JSX.Element | null {
  const menuItems = useGroupMenuOptionsMobile();
  const { formatDate } = useDateTime();

  const selectedEntities = useAppSelector(selectedEntitiesSelector("groups"));
  const selectedSdbCompany = useAppSelector(selectedSdbCompanySelector);

  const isAnyRowSelected = selectedEntities.length !== 0;

  // Extract the sort field from the sortBy prop
  const sortField = GROUPS_SORT_BY_MAP[sortBy].field;

  // The available bulk actions to display according to the table context.
  const bulkActionsButtons = useGroupsPageBulkActions({
    groups,
  });

  if (!selectedSdbCompany) {
    return null;
  }

  const allGroupIds = groups.map((group) => group.id);

  /**
   * Helper to get the appropriate subtitle based on the current sort field
   */
  function getSubtitle(group: GroupTypes): string {
    switch (sortField) {
      case "name":
      case "createdDate":
        return `Created: ${formatDate(group.createdAt)}`;
      case "members":
        return `Managers: ${getMemberCount(group)}`;
      case "projects":
        return `Projects: ${getProjectNumbersInGroup(group)}`;
      default:
        return `Created: ${formatDate(group.createdAt)}`;
    }
  }

  /**
   * Helper to get the secondary text based on counts not shown in subtitle
   */
  function getSecondaryText(group: GroupTypes): string {
    const managers = getMemberCount(group);
    const projects = getProjectNumbersInGroup(group);

    // If we're sorting by members, show projects; if by projects, show managers
    // Otherwise show both
    switch (sortField) {
      case "members":
        return `Projects: ${projects}`;
      case "projects":
        return `Managers: ${managers}`;
      default:
        return `${managers} Managers • ${projects} Projects`;
    }
  }

  return (
    <VirtualizedGrid
      // The `key` here forces React to remount the grid whenever `sortBy` changes.
      // This ensures that the scroll position resets and the grid re-renders from the first item
      key={sortBy}
      items={groups}
      isLoading={isLoading}
      renderSkeletonItem={(index) => (
        <Box key={index}>
          <SkeletonProjectListItemCard />
        </Box>
      )}
      estimateSize={112}
      renderItem={(group, index) => {
        const subtitle = getSubtitle(group);
        const secondaryText = getSecondaryText(group);

        return (
          <Box key={group.id}>
            {index !== 0 && <Divider />}

            <ListItemCardMobile<GroupTypes>
              item={group}
              image={
                group.thumbnailUrl?.length
                  ? group.thumbnailUrl
                  : defaultGroupThumbnail
              }
              imageAlt="Group thumbnail"
              title={group.name}
              href={generateGroupDetailsRoute(selectedSdbCompany.id, group.id)}
              subtitle={subtitle}
              secondaryText={secondaryText}
              menuItems={menuItems}
            />
          </Box>
        );
      }}
      shouldRenderFooterOutside={isAnyRowSelected}
      renderFooterAction={() =>
        isAnyRowSelected ? (
          <FaroTableBulkActions
            bulkActionsButtons={bulkActionsButtons}
            numberOfSelectedRows={selectedEntities.length}
            isLoading={isLoading}
            allRowIds={allGroupIds}
          />
        ) : (
          <CreateGroup groups={groups} />
        )
      }
    />
  );
}
